import {
  DEFAULT_TRANCHE_METHOD,
  faNum,
  METHOD_LABEL_FA,
  TRANCHE_METHODS,
  tranchesTotalPct,
  type PaymentTranche,
  type TrancheMethod,
} from '@kia/shared';
import { Button, Input } from '../ui';

/**
 * Payment-schedule editor (CIRCULARITY_360 C2b) — reused by the deal form and the Settings template
 * manager. Rows of (share % · anchor · offset days · label), a live 100% check, and an optional
 * template picker that loads a named template's tranches.
 */

export interface Template {
  id: number;
  name: string;
  tranches: PaymentTranche[];
  is_default: boolean;
}

export function ScheduleEditor({
  tranches,
  onChange,
  templates,
}: {
  tranches: PaymentTranche[];
  onChange: (t: PaymentTranche[]) => void;
  templates?: Template[];
}) {
  const total = tranchesTotalPct(tranches);
  const ok = Math.round(total) === 100;

  const set = (i: number, patch: Partial<PaymentTranche>) =>
    onChange(tranches.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const add = () =>
    onChange([
      ...tranches,
      { pct: 0, anchor: 'order', offset_days: 0, label: '', method: DEFAULT_TRANCHE_METHOD },
    ]);
  const remove = (i: number) => onChange(tranches.filter((_, j) => j !== i));

  return (
    <div>
      {templates && templates.length ? (
        <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <span className="muted" style={{ fontSize: 12 }}>
            بارگذاری از قالب:
          </span>
          <select
            className="input"
            style={{ maxWidth: 260 }}
            value=""
            onChange={(e) => {
              const t = templates.find((x) => x.id === Number(e.target.value));
              if (t) onChange(t.tranches.map((x) => ({ ...x })));
            }}
          >
            <option value="">— انتخاب —</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.is_default ? ' ★' : ''}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {tranches.length ? (
        <table className="table" style={{ margin: 0 }}>
          <thead>
            <tr>
              <th>شرح</th>
              <th>درصد</th>
              <th>روش</th>
              <th>مبنا</th>
              <th>روز پس از مبنا</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tranches.map((t, i) => (
              <tr key={i}>
                <td>
                  <Input
                    value={t.label}
                    onChange={(e) => set(i, { label: e.target.value })}
                    placeholder="مثلاً پیش‌پرداخت"
                  />
                </td>
                <td style={{ width: 90 }}>
                  <Input
                    type="number"
                    value={t.pct}
                    onChange={(e) => set(i, { pct: Number(e.target.value) })}
                  />
                </td>
                <td style={{ width: 130 }}>
                  <select
                    className="input"
                    value={t.method}
                    onChange={(e) => set(i, { method: e.target.value as TrancheMethod })}
                  >
                    {TRANCHE_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {METHOD_LABEL_FA[m]}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ width: 130 }}>
                  <select
                    className="input"
                    value={t.anchor}
                    onChange={(e) =>
                      set(i, { anchor: e.target.value === 'delivery' ? 'delivery' : 'order' })
                    }
                  >
                    <option value="order">از سفارش</option>
                    <option value="delivery">از تحویل</option>
                  </select>
                </td>
                <td style={{ width: 110 }}>
                  <Input
                    type="number"
                    value={t.offset_days}
                    onChange={(e) => set(i, { offset_days: Math.max(0, Number(e.target.value)) })}
                  />
                </td>
                <td style={{ width: 40 }}>
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    style={{ border: 0, background: 'transparent', cursor: 'pointer' }}
                    aria-label="حذف ردیف"
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted" style={{ margin: '4px 0' }}>
          بدون زمان‌بندی صریح — پیش‌فرض تنظیمات اعمال می‌شود.
        </p>
      )}

      <div className="row" style={{ gap: 12, alignItems: 'center', marginTop: 8 }}>
        <Button type="button" variant="secondary" onClick={add}>
          + ردیف
        </Button>
        {tranches.length ? (
          <span style={{ color: ok ? 'var(--green)' : 'var(--red)', fontSize: 13 }}>
            مجموع: {faNum(Math.round(total * 10) / 10, 1)}٪ {ok ? '✓' : '(باید ۱۰۰٪ شود)'}
          </span>
        ) : null}
      </div>
    </div>
  );
}
